import { Domains } from '@dtx-company/true-common/src/types/domains'
import { FacebookButton, GoogleButton, Text } from '@dtx-company/shared-components/src'
import { LayoutProps, SpaceProps } from 'styled-system'
import { SocialProvider } from '@dtx-company/true-common/src/types/socialProvider'
import { User } from '../../../types/user'
import { Wrapper } from './styles'
import { isSafari } from '@dtx-company/inter-app/src/utils/misc/check-browser'
import { useGoogleLoginUrl } from '@dtx-company/app/shared/common/src/hooks/useGoogleLoginUrl'
import { useSocialAuthState } from './index.hooks'
import React from 'react'
import events from '@dtx-company/inter-app/src/event-tracking/events/tagging'

export interface SocialAuthProps {
  showFacebookBtn?: boolean
  onClick: (user: User, provider: SocialProvider) => void | Promise<void>
  fireHeapEventOnClick: (provider: SocialProvider) => void
  cta: string
  checkTerms: (args: { quiet?: boolean } | void) => boolean
  product: Domains
  isSignup: boolean
}

export const SocialAuth: React.FC<SocialAuthProps & SpaceProps & LayoutProps> = ({
  onClick,
  fireHeapEventOnClick,
  cta,
  checkTerms,
  product,
  isSignup,
  showFacebookBtn = false,
  ...rest
}) => {
  const { handleClick } = useSocialAuthState({ onClick })
  const handleAuthClick = async (provider: SocialProvider): Promise<void> => {
    events.Revenue_SignUpPage_Clicked_SignUpButton(provider.toUpperCase())
    if (!checkTerms()) return
    fireHeapEventOnClick?.(provider)
    handleClick(provider, product)
  }

  const { url: googleLoginUrl } = useGoogleLoginUrl(isSignup ? 'signup' : 'login')
  const showFBButton = !isSignup && showFacebookBtn

  return (
    <Wrapper {...rest}>
      {googleLoginUrl && (
        <>
          <a
            href={!checkTerms({ quiet: true }) ? undefined : googleLoginUrl}
            // Can't use "popup" mode in Safari for social sign-in because of ITP
            // (see [SC-79704](https://app.shortcut.com/flowcode/story/79704))
            {...(!isSafari() && {
              target: '_blank',
              rel: 'noopener noreferrer'
            })}
          >
            <GoogleButton
              type="button"
              width="100%"
              onClick={() => handleAuthClick(SocialProvider.GOOGLE)}
            >
              <Text variant="body/small" fontWeight={600} color="primary.black">
                {cta} Google
              </Text>
            </GoogleButton>
          </a>
        </>
      )}
      {showFBButton && (
        <FacebookButton
          type="button"
          width="100%"
          onClick={() => {
            handleAuthClick(SocialProvider.FACEBOOK)
          }}
        >
          <Text variant="body/small" fontWeight={600} color="primary.black">
            {cta} Facebook
          </Text>
        </FacebookButton>
      )}
    </Wrapper>
  )
}
